import {computed, type ComputedRef} from 'vue';
import {useI18n} from 'vue-i18n';
import {useRouter} from 'vue-router';
import {useLang, SUPPORTED_LANGUAGES} from '@/ts/composables/stateful/use-lang';
import {useI18nRouter} from '@/ts/composables/stateful/use-i18n-router';
import {useApi} from '@/ts/composables/stateful/use-api';
import {Language} from '@/ts/types/component/localization.type';
import {persistentStorage} from '@/ts/instances/persistant-storage';
import {LANGUAGE_REGEX} from '@/ts/utils/consts';

interface LanguageOption {
  label: string;
  value: Language;
}

const LANGUAGE_LABEL_KEYS: Record<Language, string> = {
  [Language.English]: 'english',
  [Language.German]: 'german',
};

/**
 * Shared language-change flow used by every language-switching surface (admin profile,
 * user preferences page, header dropdown). Exposes localized dropdown options, a safe
 * initial language getter, and a finalizer that updates the route's language prefix and
 * reloads the page so Vue Router and Vue I18n stay in sync.
 *
 * Callers are responsible for persisting `languageCode` to the backend (e.g. via
 * `api.me.updateMe`) before invoking `applyLanguageChange` when the user is authenticated.
 */
export function useLanguageChange(): {
  languageOptions: ComputedRef<LanguageOption[]>;
  currentLanguage: ComputedRef<LanguageOption>;
  getInitialLanguage(): Language;
  applyLanguageChange(newLanguage: Language): Promise<void>;
  syncBackendLanguage(backendLanguage: string | null | undefined): boolean;
} {
  const {t} = useI18n();
  const {lang} = useLang();
  const {updateRouteLanguage} = useI18nRouter();
  const router = useRouter();
  const api = useApi();

  const languageOptions = computed<LanguageOption[]>(() => SUPPORTED_LANGUAGES.map((language) => ({
    label: t(LANGUAGE_LABEL_KEYS[language]),
    value: language,
  })));

  const currentLanguage = computed<LanguageOption>(() =>
    languageOptions.value.find((option) => option.value === lang.value) ?? languageOptions.value[0]);

  function getInitialLanguage(): Language {
    return SUPPORTED_LANGUAGES.includes(lang.value) ? lang.value : Language.English;
  }

  async function applyLanguageChange(newLanguage: Language): Promise<void> {
    if (newLanguage === lang.value) {
      return;
    }
    await updateRouteLanguage(newLanguage);
    router.go(0);
  }

  /**
   * Aligns the app with the backend language preference returned by `/me`. When valid,
   * persists it to storage and (if it differs from the active locale) reloads onto the
   * matching language URL. When invalid, pushes the active locale back to the backend.
   * Returns `true` when a redirect was issued so callers can short-circuit further work.
   */
  function syncBackendLanguage(backendLanguage: string | null | undefined): boolean {
    if (!backendLanguage) {
      return false;
    }
    if (!SUPPORTED_LANGUAGES.includes(backendLanguage as Language)) {
      void api.me.updateMe({languageCode: lang.value});
      return false;
    }
    const target = backendLanguage as Language;
    persistentStorage.language = target;
    if (target === lang.value) {
      return false;
    }
    const currentPath = window.location.pathname;
    const newPath = currentPath.replace(LANGUAGE_REGEX.REMOVE_LANG_PREFIX, `/${target}`);
    window.location.replace(`${window.location.origin}${newPath}${window.location.search}`);
    return true;
  }

  return {
    languageOptions,
    currentLanguage,
    getInitialLanguage,
    applyLanguageChange,
    syncBackendLanguage,
  };
}
